/**
 * BulkEditModal — edit common fields across multiple selected items.
 * Works for both Test Scenarios and Test Cases.
 */
import React, { useState } from 'react';
import './BulkEditModal.css';

const PRIORITIES = ['High', 'Medium', 'Low'];
const ENVIRONMENTS = ['Stage', 'Preprod', 'Prod'];
const ALL_TAGS = ['Smoke', 'Sanity', 'Integration', 'Manual', 'API', 'UI', 'Performance', 'Security', 'Functional', 'Regression', 'AI Generated'];

export interface BulkEditPayload {
  priority?: string;
  environment?: string;
  tags_add?: string[];
  tags_remove?: string[];
}

interface BulkEditModalProps {
  count: number;
  type: 'scenario' | 'test_case';
  onApply: (payload: BulkEditPayload) => Promise<void>;
  onCancel: () => void;
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({ count, type, onApply, onCancel }) => {
  const [priority, setPriority] = useState('');
  const [environment, setEnvironment] = useState('');
  const [tagsAdd, setTagsAdd] = useState<string[]>([]);
  const [tagsRemove, setTagsRemove] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const hasChanges = priority || environment || tagsAdd.length > 0 || tagsRemove.length > 0;

  const toggleTagAdd = (tag: string) => {
    setTagsAdd(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setTagsRemove(prev => prev.filter(t => t !== tag)); // can't add and remove same tag
  };

  const toggleTagRemove = (tag: string) => {
    setTagsRemove(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setTagsAdd(prev => prev.filter(t => t !== tag));
  };

  const handleApply = async () => {
    if (!hasChanges) { setError('Please make at least one change'); return; }
    setIsLoading(true);
    setError('');
    try {
      const payload: BulkEditPayload = {};
      if (priority) payload.priority = priority;
      if (environment) payload.environment = environment;
      if (tagsAdd.length > 0) payload.tags_add = tagsAdd;
      if (tagsRemove.length > 0) payload.tags_remove = tagsRemove;
      await onApply(payload);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bem-overlay" onClick={onCancel}>
      <div className="bem-modal" onClick={e => e.stopPropagation()}>
        <div className="bem-header">
          <div>
            <h3 className="bem-title">Bulk Edit</h3>
            <p className="bem-subtitle">Editing {count} selected {type === 'scenario' ? 'scenario' : 'test case'}{count > 1 ? 's' : ''}</p>
          </div>
          <button className="bem-close" onClick={onCancel}>×</button>
        </div>

        <div className="bem-body">
          {error && <div className="bem-error">{error}</div>}

          {/* Priority */}
          <div className="bem-field">
            <label className="bem-label">PRIORITY <span className="bem-optional">(leave blank = no change)</span></label>
            <div className="bem-options">
              {PRIORITIES.map(p => (
                <button
                  key={p}
                  type="button"
                  className={`bem-option ${priority === p ? 'active' : ''}`}
                  onClick={() => setPriority(priority === p ? '' : p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Environment — scenarios only */}
          {type === 'scenario' && (
            <div className="bem-field">
              <label className="bem-label">ENVIRONMENT <span className="bem-optional">(leave blank = no change)</span></label>
              <div className="bem-options">
                {ENVIRONMENTS.map(e => (
                  <button
                    key={e}
                    type="button"
                    className={`bem-option ${environment === e ? 'active' : ''}`}
                    onClick={() => setEnvironment(environment === e ? '' : e)}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="bem-field">
            <label className="bem-label">TAGS</label>
            <div className="bem-tags-grid">
              {ALL_TAGS.map(tag => {
                const adding = tagsAdd.includes(tag);
                const removing = tagsRemove.includes(tag);
                return (
                  <div key={tag} className="bem-tag-row">
                    <span className="bem-tag-name">{tag}</span>
                    <div className="bem-tag-actions">
                      <button
                        type="button"
                        className={`bem-tag-btn add ${adding ? 'active' : ''}`}
                        onClick={() => toggleTagAdd(tag)}
                        title="Add this tag"
                      >+ Add</button>
                      <button
                        type="button"
                        className={`bem-tag-btn remove ${removing ? 'active' : ''}`}
                        onClick={() => toggleTagRemove(tag)}
                        title="Remove this tag"
                      >− Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bem-footer">
          <button className="bem-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="bem-btn-apply"
            onClick={handleApply}
            disabled={isLoading || !hasChanges}
          >
            {isLoading ? 'Applying...' : `Apply to ${count} item${count > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
